"use client";

import { Phone, PhoneCall, PhoneOutgoing, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, Play, Pause, Download, Mic, MicOff, Loader2, X } from "lucide-react";
import React, { useState } from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { getProxyRecordingUrl, downloadRecording } from "@/app/_module/lib/utils";
import { RecordingPlayer } from "@/app/_module/components/RecordingPlayer";
import type { CallRecording } from "@/app/_module/contexts/CallRecordingContext";
import { CallTranscriptPanel } from "./CallTranscriptPanel";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/app/_module/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/app/_module/components/ui/table";
import { DataPagination, type PaginationMeta } from "@/app/_module/components/ui/data-pagination";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import {
    Dialog,
    ResponsiveDialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { toast } from "@/app/_module/hooks/use-toast";

// Import unified types
import type { CallHistoryItem } from "@/app/(authenticated)/communication/_modules/types/communication-history.types";
import { determineCallStatus } from "@/app/(authenticated)/communication/_modules/utils/transform-communication-data";

/**
 * Display status for a call row — NOT the raw `call_status`, which is
 * 'completed' for voicemails and therefore renders them as "Answered".
 * Mirrors the Communications list and the lead History tab.
 */
const displayStatusOf = (call: CallHistoryItem): string =>
  determineCallStatus(
    call,
    call.callType === "incoming" || call.callType === "inbound" ? "inbound" : "outbound",
  );

// Format phone number to (xxx) xxx-xxxx format
const formatPhoneNumber = (phone: string): string => {
    if (!phone) return "";
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, "");
    // Handle 10-digit numbers
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    // Handle 11-digit numbers (with country code)
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
        return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    // Return original if format doesn't match
    return phone;
};

interface LeadCallHistoryProps {
    leadId: string;
    /** Used to name downloaded recording files (falls back to phone, then "unknown"). */
    leadName?: string;
    calls?: CallHistoryItem[];
    isLoading?: boolean;
    onCallClick?: (call: CallHistoryItem) => void;
    onCallBack?: (phoneNumber: string) => void;
    onPlayRecording?: (recordingUrl: string) => void;
    onRefresh?: () => Promise<void>;
    paginationMeta?: PaginationMeta;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    embedded?: boolean; // When true, hide outer Card wrapper
}

// Compact audio player for embedded views
// How the call was placed — same chip styling as Recent Activity's dialer badges
export const DialerModeBadge: React.FC<{ mode?: string | null; className?: string }> = ({ mode, className = "" }) => {
    const m = mode || "manual";
    const cls =
        m === "power_dialer"
            ? "bg-violet-50 text-violet-700"
            : m === "multi_line"
                ? "bg-blue-50 text-blue-700"
                : "bg-gray-100 text-gray-600";
    const label = m === "power_dialer" ? "Power Dialer" : m === "multi_line" ? "Multi-Line" : "Manual";
    return (
        <Badge variant="secondary" className={`h-5 border-0 px-1.5 text-[10px] font-semibold ${cls} ${className}`}>
            {label}
        </Badge>
    );
};

/** A lead's call as the app-wide player's recording (shared by History and this table). */
export const toCallRecording = (
    call: CallHistoryItem & { recordingUrl: string },
    lead: { leadId?: string | null; leadName?: string },
): CallRecording => ({
    callId: call.id,
    recordingUrl: call.recordingUrl,
    title: lead.leadName || formatPhoneNumber(call.phoneNumber) || "Unknown caller",
    direction: call.callType === "incoming" || call.callType === "inbound" ? "inbound" : "outbound",
    callDate: call.callDate,
    // Transcription and the scrubber are bounded by the RECORDING, not the call.
    durationSecs: call.recordingDuration || call.duration,
    leadId: lead.leadId,
});

export const CompactAudioPlayer: React.FC<{ recording: CallRecording }> = ({ recording }) => {
    // Thin wrapper around the shared RecordingPlayer so the History tab, this
    // table and the Agent Details card play (and name downloads) identically.
    const day = recording.callDate ? String(recording.callDate).slice(0, 10) : "";
    return (
        <RecordingPlayer
            recording={recording}
            downloadName={`${recording.title.replace(/\s+/g, "_")}_${day}.mp3`}
        />
    );
};

// Constants for batch download
const MAX_BATCH_SIZE = 50;
const DOWNLOAD_DELAY = 500; // ms between downloads to avoid browser blocking

export const LeadCallHistory: React.FC<LeadCallHistoryProps> = function ({
    leadId,
    leadName,
    calls = [],
    isLoading = false,
    onCallClick,
    onCallBack,
    onPlayRecording,
    onRefresh,
    paginationMeta,
    onPageChange,
    onPageSizeChange,
    embedded = false,
}) {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [expandedCallId, setExpandedCallId] = useState<string | null>(null);

    // Batch selection state
    const [selectedCallIds, setSelectedCallIds] = useState<Set<string>>(new Set());
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Get calls with recordings
    const callsWithRecordings = calls.filter(call => call.recordingUrl);
    const selectedCalls = calls.filter(call => selectedCallIds.has(call.id) && call.recordingUrl);
    const hasSelection = selectedCallIds.size > 0;
    const allSelected = callsWithRecordings.length > 0 && callsWithRecordings.every(call => selectedCallIds.has(call.id));

    const handleRefresh = async () => {
        if (onRefresh && !isRefreshing) {
            setIsRefreshing(true);
            try {
                await onRefresh();
            } finally {
                setIsRefreshing(false);
            }
        }
    };

    // Toggle individual selection
    const toggleSelection = (callId: string) => {
        setSelectedCallIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(callId)) {
                newSet.delete(callId);
            } else {
                newSet.add(callId);
            }
            return newSet;
        });
    };

    // Toggle select all
    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedCallIds(new Set());
        } else {
            setSelectedCallIds(new Set(callsWithRecordings.map(call => call.id)));
        }
    };

    // Clear selection
    const clearSelection = () => {
        setSelectedCallIds(new Set());
    };

    // Download single recording (used by the bulk-download loop). Same JWT-aware
    // fetch as the shared helper; returns false so batch progress keeps advancing
    // even if one recording fails.
    const downloadOneRecording = async (call: CallHistoryItem): Promise<boolean> => {
        if (!call.recordingUrl) return false;
        try {
            const proxyUrl = getProxyRecordingUrl(call.recordingUrl) || call.recordingUrl;
            const callDateObj = new Date(call.callDate);
            const localDateStr = `${callDateObj.getFullYear()}-${String(callDateObj.getMonth() + 1).padStart(2, '0')}-${String(callDateObj.getDate()).padStart(2, '0')}`;
            // Prefer the lead's name (which all calls in this view belong to)
            // over the phone number — same lead, all rows.
            const fileName = `recording-${leadName || call.phoneNumber || "unknown"}-${localDateStr}.mp3`;
            await downloadRecording(proxyUrl, fileName);
            return true;
        } catch {
            return false;
        }
    };

    // Batch download handler
    const handleBatchDownload = async () => {
        if (selectedCallIds.size === 0) return;

        // Show confirmation for large batches
        if (selectedCallIds.size > 10 && !showConfirmDialog) {
            setShowConfirmDialog(true);
            return;
        }

        setShowConfirmDialog(false);
        setIsDownloading(true);
        setDownloadProgress({ current: 0, total: selectedCalls.length });

        const failures: string[] = [];
        let successCount = 0;

        for (let i = 0; i < selectedCalls.length; i++) {
            const call = selectedCalls[i];
            setDownloadProgress({ current: i + 1, total: selectedCalls.length });

            const success = await downloadOneRecording(call);

            if (success) {
                successCount++;
            } else {
                failures.push(call.phoneNumber || call.id);
            }

            // Add delay between downloads to avoid browser blocking
            if (i < selectedCalls.length - 1) {
                await new Promise(resolve => setTimeout(resolve, DOWNLOAD_DELAY));
            }
        }

        setIsDownloading(false);
        setDownloadProgress({ current: 0, total: 0 });

        // Show results
        if (failures.length === 0) {
            toast({
                title: "Download Complete",
                description: `Successfully downloaded ${successCount} recording${successCount !== 1 ? 's' : ''}`,
            });
        } else {
            toast({
                title: "Download Completed with Errors",
                description: `Downloaded ${successCount} of ${selectedCalls.length} recordings. ${failures.length} failed.`,
                variant: "destructive",
            });
        }

        clearSelection();
    };
    const getStatusIcon = (status: string) => {
        switch (status) {
            case "answered":
            case "completed":
                return <CheckCircle className="h-4 w-4 text-green-600" />;
            case "missed":
            case "no-answer":
            case "no_answer":
                return <XCircle className="h-4 w-4 text-red-600" />;
            case "declined":
                return <XCircle className="h-4 w-4 text-orange-600" />;
            case "failed":
                return <AlertCircle className="h-4 w-4 text-red-600" />;
            case "voicemail":
                return <Phone className="h-4 w-4 text-purple-600" />;
            case "busy":
                return <Phone className="h-4 w-4 text-orange-600" />;
            default:
                return <Phone className="h-4 w-4 text-gray-600" />;
        }
    };

    const getStatusBadge = (status: string) => {
        const variants: Record<string, string> = {
            answered: "bg-green-100 text-green-800",
            completed: "bg-green-100 text-green-800",
            missed: "bg-red-100 text-red-800",
            declined: "bg-orange-100 text-orange-800",
            failed: "bg-red-100 text-red-800",
            voicemail: "bg-purple-100 text-purple-800",
            busy: "bg-orange-100 text-orange-800",
            "no-answer": "bg-red-100 text-red-800",
            "no_answer": "bg-red-100 text-red-800",
            canceled: "bg-gray-100 text-gray-800",
        };

        const labels: Record<string, string> = {
            completed: "Answered",
            "no-answer": "No Answer",
            "no_answer": "No Answer",
        };

        const label = labels[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown');

        return (
            <Badge className={variants[status] || "bg-gray-100 text-gray-800"}>
                {label}
            </Badge>
        );
    };

    const formatDuration = (seconds: number) => {
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getRecordingStatus = (call: CallHistoryItem) => {
        if (call.recordingUrl) {
            return "available";
        }
        // If the call was answered and happened within the last 5 minutes, recording might still be processing
        const callDate = new Date(call.callDate);
        const now = new Date();
        const minutesSinceCall = (now.getTime() - callDate.getTime()) / 1000 / 60;

        if (call.status === "answered" && call.duration > 0 && minutesSinceCall < 5) {
            return "processing";
        }

        return "none";
    };

    const getRecordingBadge = (status: "available" | "processing" | "none") => {
        switch (status) {
            case "available":
                return (
                    <Badge className="bg-green-100 text-green-800 gap-1">
                        <Mic className="h-3 w-3" />
                        Recording
                    </Badge>
                );
            case "processing":
                return (
                    <Badge className="bg-yellow-100 text-yellow-800 gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Processing
                    </Badge>
                );
            case "none":
                return (
                    <Badge variant="outline" className="text-gray-500 gap-1">
                        <MicOff className="h-3 w-3" />
                        No Recording
                    </Badge>
                );
        }
    };

    const toggleCallExpanded = (callId: string) => {
        setExpandedCallId(expandedCallId === callId ? null : callId);
    };

    if (isLoading) {
        const loadingContent = (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading call history...</span>
            </div>
        );

        if (embedded) return loadingContent;

        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <PhoneCall className="h-5 w-5" />
                        Call History
                    </CardTitle>
                </CardHeader>
                <CardContent>{loadingContent}</CardContent>
            </Card>
        );
    }

    // Compact card-based view for embedded mode
    if (embedded) {
        if (calls.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Phone className="h-10 w-10 text-gray-400 mb-3" />
                    <p className="text-sm text-gray-600">No calls yet</p>
                </div>
            );
        }

        return (
            <div className="space-y-2">
                {calls.map((call) => {
                    const recordingStatus = getRecordingStatus(call);
                    const isExpanded = expandedCallId === call.id;

                    return (
                        <div key={call.id} className="border rounded-lg overflow-hidden">
                            <div
                                className={`p-3 hover:bg-gray-50 ${call.recordingUrl ? 'cursor-pointer' : ''}`}
                                onClick={() => call.recordingUrl && toggleCallExpanded(call.id)}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        {call.callType === "incoming" ? (
                                            <Phone className="h-4 w-4 text-green-600 shrink-0" />
                                        ) : (
                                            <PhoneCall className="h-4 w-4 text-blue-600 shrink-0" />
                                        )}
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <p className="text-sm font-medium">
                                                    {call.callType === "incoming" ? "Inbound" : "Outbound"}
                                                </p>
                                                <DialerModeBadge mode={call.dialerMode} />
                                            </div>
                                            <p className="text-xs text-gray-500">
                                                {formatDate(call.callDate)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {getStatusBadge(displayStatusOf(call))}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between mt-2 pt-2 border-t">
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <span className="flex items-center gap-1">
                                            <Clock className="h-3 w-3" />
                                            {formatDuration(call.duration)}
                                        </span>
                                        {/* Which of OUR numbers the call went through. Flips with
                                            direction: outbound = the number we dialled FROM, inbound =
                                            the number they dialled TO. On inbound, `fromNumber` is the
                                            CLIENT's number and is already this card's header, so showing
                                            it would just repeat the lead's own phone. */}
                                        {(() => {
                                            const isInbound =
                                                call.callType === "incoming" || call.callType === "inbound";
                                            const num = isInbound ? call.toNumber : call.fromNumber;
                                            if (!num) return null;
                                            return (
                                                <span
                                                    className="flex items-center gap-1"
                                                    title={
                                                        isInbound
                                                            ? "Number they called"
                                                            : "Number this call was placed from"
                                                    }
                                                >
                                                    <PhoneOutgoing className="h-3 w-3" />
                                                    {isInbound ? "to " : ""}
                                                    {formatPhoneNumber(num)}
                                                </span>
                                            );
                                        })()}
                                        {recordingStatus === "available" && (
                                            <Badge className="bg-green-100 text-green-800 text-xs py-0 px-1.5">
                                                <Mic className="h-3 w-3 mr-0.5" />
                                                Rec
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {(call.callType === "incoming" || call.callType === "inbound") && onCallBack && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onCallBack(call.phoneNumber);
                                                }}
                                                className="h-7 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                                            >
                                                <Phone className="h-3 w-3 mr-1" />
                                                Call Back
                                            </Button>
                                        )}
                                        {call.recordingUrl && (
                                            <Button
                                                size="sm"
                                                variant={isExpanded ? "default" : "ghost"}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleCallExpanded(call.id);
                                                }}
                                                className="h-7 px-2 text-xs"
                                            >
                                                {isExpanded ? <Pause className="h-3 w-3 mr-1" /> : <Play className="h-3 w-3 mr-1" />}
                                                {isExpanded ? "Hide" : "Play"}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {isExpanded && call.recordingUrl && (
                                <div className="border-t bg-gray-50 p-2">
                                    <CompactAudioPlayer recording={toCallRecording({ ...call, recordingUrl: call.recordingUrl }, { leadId, leadName })} />
                                    <CallTranscriptPanel recording={toCallRecording({ ...call, recordingUrl: call.recordingUrl }, { leadId, leadName })} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    const mainContent = (
        <>
                {calls.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Phone className="h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No calls yet</h3>
                        <p className="text-gray-600 mb-4">
                            Call history will appear here once you start making calls to this lead.
                        </p>
                        <Button className="bg-blue-600 hover:bg-blue-700">
                            <Phone className="h-4 w-4 mr-2" />
                            Make First Call
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* Batch Actions Toolbar */}
                        {hasSelection && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-blue-900">
                                        {selectedCallIds.size} recording{selectedCallIds.size !== 1 ? 's' : ''} selected
                                    </span>
                                    {isDownloading && (
                                        <span className="text-sm text-blue-700">
                                            Downloading {downloadProgress.current} of {downloadProgress.total}...
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        onClick={handleBatchDownload}
                                        disabled={isDownloading || selectedCallIds.size > MAX_BATCH_SIZE}
                                        className="bg-blue-600 hover:bg-blue-700 gap-2"
                                    >
                                        {isDownloading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Downloading...
                                            </>
                                        ) : (
                                            <>
                                                <Download className="h-4 w-4" />
                                                Download Selected
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={clearSelection}
                                        disabled={isDownloading}
                                        className="gap-2"
                                    >
                                        <X className="h-4 w-4" />
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        )}

                        {selectedCallIds.size > MAX_BATCH_SIZE && (
                            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                <p className="text-sm text-orange-900">
                                    Maximum batch size is {MAX_BATCH_SIZE} recordings. Please reduce your selection.
                                </p>
                            </div>
                        )}

                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        {callsWithRecordings.length > 0 && (
                                            <TableHead className="w-12">
                                                <Checkbox
                                                    checked={allSelected}
                                                    onCheckedChange={toggleSelectAll}
                                                    disabled={isDownloading}
                                                    aria-label="Select all recordings"
                                                />
                                            </TableHead>
                                        )}
                                        <TableHead>Type</TableHead>
                                        <TableHead>Method</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Duration</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Outcome</TableHead>
                                        <TableHead>Recording</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {calls.map((call) => {
                                        const recordingStatus = getRecordingStatus(call);
                                        const isExpanded = expandedCallId === call.id;
                                        const hasRecording = !!call.recordingUrl;
                                        const isSelected = selectedCallIds.has(call.id);

                                        return (
                                            <React.Fragment key={call.id}>
                                                <TableRow
                                                    className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''} ${call.recordingUrl ? 'cursor-pointer' : ''}`}
                                                    onClick={() => {
                                                        if (call.recordingUrl) {
                                                            toggleCallExpanded(call.id);
                                                        }
                                                    }}
                                                >
                                                    {callsWithRecordings.length > 0 && (
                                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                                            {hasRecording && (
                                                                <Checkbox
                                                                    checked={isSelected}
                                                                    onCheckedChange={() => toggleSelection(call.id)}
                                                                    disabled={isDownloading}
                                                                    aria-label={`Select recording for ${call.phoneNumber}`}
                                                                />
                                                            )}
                                                        </TableCell>
                                                    )}
                                                    <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {call.callType === "incoming" ? (
                                                            <Phone className="h-4 w-4 text-green-600" />
                                                        ) : (
                                                            <PhoneCall className="h-4 w-4 text-blue-600" />
                                                        )}
                                                        <span className="capitalize">{call.callType === "incoming" ? "inbound" : "outbound"}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <DialerModeBadge mode={call.dialerMode} />
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {getStatusIcon(displayStatusOf(call))}
                                                        {getStatusBadge(displayStatusOf(call))}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="h-4 w-4 text-gray-400" />
                                                        {formatDuration(call.duration)}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(call.callDate)}
                                                </TableCell>
                                                <TableCell>
                                                    {call.outcome ? (
                                                        <Badge variant="outline">
                                                            {call.outcome
                                                                .split("_")
                                                                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                                                .join(" ")}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {getRecordingBadge(recordingStatus)}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {/* Call Back button for inbound calls */}
                                                        {(call.callType === "incoming" || call.callType === "inbound") && onCallBack && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onCallBack(call.phoneNumber);
                                                                }}
                                                                className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                                                            >
                                                                <Phone className="h-3 w-3" />
                                                                Call Back
                                                            </Button>
                                                        )}
                                                        {call.recordingUrl && (
                                                            <Button
                                                                size="sm"
                                                                variant={isExpanded ? "default" : "ghost"}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleCallExpanded(call.id);
                                                                }}
                                                                className="gap-1"
                                                            >
                                                                {isExpanded ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                                                                {isExpanded ? "Hide" : "Play"}
                                                            </Button>
                                                        )}
                                                        {call.followUpRequired && (
                                                            <Badge variant="outline" className="text-orange-600">
                                                                Follow-up
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                            {isExpanded && call.recordingUrl && (
                                                <TableRow>
                                                    <TableCell colSpan={callsWithRecordings.length > 0 ? 9 : 8} className="bg-gray-50/50 p-4">
                                                        <CompactAudioPlayer recording={toCallRecording({ ...call, recordingUrl: call.recordingUrl }, { leadId, leadName })} />
                                                        <CallTranscriptPanel recording={toCallRecording({ ...call, recordingUrl: call.recordingUrl }, { leadId, leadName })} />
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                    </>
                )}

                {/* Confirmation Dialog for Large Batches */}
                <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                    <ResponsiveDialogContent size="md">
                        <DialogHeader>
                            <DialogTitle>Download {selectedCallIds.size} Recordings?</DialogTitle>
                            <DialogDescription>
                                You are about to download {selectedCallIds.size} recordings. This may take a few moments and will download each file individually.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setShowConfirmDialog(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleBatchDownload}
                                className="bg-blue-600 hover:bg-blue-700"
                            >
                                Download All
                            </Button>
                        </DialogFooter>
                    </ResponsiveDialogContent>
                </Dialog>

                {paginationMeta && onPageChange && !embedded && (
                    <DataPagination
                        meta={paginationMeta}
                        onPageChange={onPageChange}
                        onPageSizeChange={onPageSizeChange}
                        isLoading={isLoading}
                        showPageSize={true}
                    />
                )}
        </>
    );

    if (embedded) return mainContent;

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <PhoneCall className="h-5 w-5" />
                        Call History ({paginationMeta?.totalItems ?? calls.length})
                    </CardTitle>
                    {onRefresh && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleRefresh}
                            disabled={isRefreshing || isLoading}
                            className="gap-2"
                        >
                            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            {isRefreshing ? 'Refreshing...' : 'Refresh'}
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent>
                {mainContent}
            </CardContent>
        </Card>
    );
};

export default LeadCallHistory;
